import { authConfig } from '@/features/auth/config'
import { AuthApiError } from '@/features/auth/services/auth-service'
import { localizeBillingPlan } from '@/features/billing/utils/membership-labels'
import type {
  BillingOptionsPayload,
  BillingOrderResult,
  BillingPlan,
  BillingStatusPayload,
} from '@/features/billing/types'

async function requestJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.headers ?? {}),
    },
  })

  const payload = (await response.json().catch(() => null)) as T | { message?: string } | null

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && payload && 'message' in payload
        ? String(payload.message)
        : '会员接口请求失败'
    throw new AuthApiError(message, response.status)
  }

  if (payload === null) {
    throw new AuthApiError('会员接口未返回 JSON 数据', response.status)
  }

  return payload as T
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function normalizePlan(plan: unknown): BillingPlan {
  if (!isRecord(plan)) {
    throw new AuthApiError('会员套餐返回格式不正确')
  }

  const planCode =
    typeof plan.plan_code === 'string'
      ? plan.plan_code
      : typeof plan.planCode === 'string'
        ? plan.planCode
        : null
  const planName =
    typeof plan.plan_name === 'string'
      ? plan.plan_name
      : typeof plan.planName === 'string'
        ? plan.planName
        : null

  if (!planCode || !planName) {
    throw new AuthApiError('会员套餐缺少必要字段')
  }

  return {
    plan_code: planCode,
    plan_name: planName,
    product_code:
      typeof plan.product_code === 'string'
        ? plan.product_code
        : typeof plan.productCode === 'string'
          ? plan.productCode
          : null,
    billing_cycle:
      typeof plan.billing_cycle === 'string'
        ? plan.billing_cycle
        : typeof plan.billingCycle === 'string'
          ? plan.billingCycle
          : null,
    price: String(plan.price ?? '0'),
    original_price:
      typeof plan.original_price === 'string'
        ? plan.original_price
        : typeof plan.originalPrice === 'string'
          ? plan.originalPrice
          : plan.original_price === null || plan.originalPrice === null
            ? null
            : null,
    duration_days: Number(plan.duration_days ?? plan.durationDays ?? 0),
    description:
      typeof plan.description === 'string'
        ? plan.description
        : plan.description == null
          ? null
          : String(plan.description),
    sort_order: Number(plan.sort_order ?? plan.sortOrder ?? 0),
    feature_flags: Array.isArray(plan.feature_flags)
      ? plan.feature_flags.filter((item): item is string => typeof item === 'string')
      : Array.isArray(plan.featureFlags)
        ? plan.featureFlags.filter((item): item is string => typeof item === 'string')
        : [],
  }
}

export function fetchBillingOptions() {
  return requestJson<BillingOptionsPayload>(authConfig.billingOptionsEndpoint, {
    method: 'GET',
  }).then((payload) => ({
    ...payload,
    plans: payload.plans.map(normalizePlan).map(localizeBillingPlan),
  }))
}

export function createBillingOrder(input: {
  planCode: string
  provider: 'alipay' | 'wechat'
  deviceFingerprint?: string
  source?: 'main-site' | 'subsite-videocut'
}) {
  return requestJson<BillingOrderResult>(authConfig.paymentCreateOrderEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...input,
      productCode: 'fastcut',
      productPlanCode: input.planCode,
    }),
  })
}

export function fetchBillingOrderStatus(orderNo: string) {
  return requestJson<BillingStatusPayload>(
    `${authConfig.paymentStatusBaseEndpoint}/${encodeURIComponent(orderNo)}`,
    {
      method: 'GET',
    },
  )
}
